import React, { useRef } from "react";
import Icon from "./Icon";
import Magnetic from "./Magnetic";

const VARIANTS = {
  primary:
    "bg-[linear-gradient(135deg,#7B1BC4_0%,#6A0DAD_50%,#4C087D_100%)] text-white shadow-[0_18px_35px_-16px_rgba(106,13,173,0.75)] hover:shadow-[0_26px_50px_-18px_rgba(106,13,173,0.9)] focus-visible:ring-[#6A0DAD]/25",
  light:
    "bg-white text-[#3B0764] shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)] hover:bg-[#F8F1FF] hover:shadow-[0_26px_55px_-18px_rgba(0,0,0,0.7)] focus-visible:ring-white/40",
  glass:
    "mw-glass-dark text-white hover:border-white/35 focus-visible:ring-white/25",
  outline:
    "border border-[#DFD5C6] bg-white text-[#1E1B16] hover:border-[#C9A9E8] hover:text-[#6A0DAD] focus-visible:ring-[#6A0DAD]/15"
};

/** The sheen that wipes across the face on hover. Skipped on `outline`,
    which is deliberately the quiet one. */
const SHEEN =
  "before:absolute before:inset-0 before:-translate-x-full before:bg-[linear-gradient(100deg,transparent_35%,rgba(255,255,255,0.28)_50%,transparent_65%)] before:transition-transform before:duration-[900ms] before:ease-[cubic-bezier(0.22,1,0.36,1)] hover:before:translate-x-full";

const SIZES = {
  md: "px-6 py-3 text-[15px]",
  lg: "px-8 py-4 text-base"
};

/**
 * Primary action button. Paints a ripple from the click point, wipes a sheen
 * across the face on hover, and animates a trailing arrow when `icon="arrow"`
 * is used.
 *
 * `magnetic` wraps it so it leans towards the pointer — reserved for the one
 * or two calls to action that lead a section, since the effect stops meaning
 * anything if every button on the page does it.
 */
function Button({
  children,
  onClick,
  variant = "primary",
  size = "lg",
  icon,
  className = "",
  type = "button",
  href,
  external = false,
  magnetic = false,
  ...rest
}) {
  const ref = useRef(null);

  // With `href` this renders a real anchor, so middle-click and
  // "open in new tab" behave the way people expect from a link.
  const Tag = href ? "a" : "button";
  const tagProps = href
    ? { href, ...(external ? { target: "_blank", rel: "noreferrer noopener" } : {}) }
    : { type };

  const handleClick = (event) => {
    const node = ref.current;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (node && !reduced) {
      const rect = node.getBoundingClientRect();
      const ripple = document.createElement("span");
      const diameter = Math.max(rect.width, rect.height) * 2;

      ripple.className = "mw-ripple";
      ripple.style.width = `${diameter}px`;
      ripple.style.height = `${diameter}px`;
      ripple.style.left = `${event.clientX - rect.left - diameter / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - diameter / 2}px`;
      ripple.addEventListener("animationend", () => ripple.remove());
      node.appendChild(ripple);
    }

    if (onClick) onClick(event);
  };

  const button = (
    <Tag
      ref={ref}
      {...tagProps}
      onClick={handleClick}
      className={`group relative isolate inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl font-semibold transition-[transform,box-shadow,background-color,border-color] duration-500 [transition-timing-function:var(--mw-spring)] focus:outline-none focus-visible:ring-4 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${
        variant === "outline" ? "" : SHEEN
      } ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
      {...rest}
    >
      <span className="relative z-10 inline-flex items-center gap-2">
        {children}
        {icon && (
          <Icon
            name={icon}
            className={`h-[18px] w-[18px] transition-transform duration-500 [transition-timing-function:var(--mw-spring)] ${
              icon === "arrow" ? "group-hover:translate-x-1" : ""
            }`}
          />
        )}
      </span>
    </Tag>
  );

  return magnetic ? <Magnetic>{button}</Magnetic> : button;
}

export default Button;
